import { Button, Icon, Popover, PopoverContent, PopoverTrigger, useIsMobile } from "@elit-hair/elit-ui";
import { t } from "i18next";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { TbBell, TbBellOff } from "react-icons/tb";
import { toast } from "sonner";

import { NotificationCenterItem } from "@/components/notifications/notification-center-item";
import { useOpenLeadPanel } from "@/hooks/use-lead-panel";
import { cn } from "@/lib/utils";
import { useNotification } from "@/providers/general/notification-provider-wrapper";
import { useCall } from "@/providers/hooks/use-call";
import { AppointmentNotificationType } from "@/types/call.types";
import { isSafariBrowser } from "@/utils/is-safari-browser";

type NotificationCenterItemData =
    | {
          kind: "appointment";
          id: string;
          receivedAt: DateTime;
          appointmentId: string;
          brandName: string;
          leadId?: string;
      }
    | {
          kind: "incident";
          id: string;
          receivedAt: DateTime;
          incidentId: string;
          title: string;
          body: string;
      };

export const HeaderNotifications = () => {
    const [open, setOpen] = useState<boolean>(false);
    const isMobile = useIsMobile();
    const openLeadPanel = useOpenLeadPanel();
    const { isCallActive } = useCall();
    const {
        pendingAppointmentNotifications,
        pendingIncidentNotifications,
        dismissAppointmentNotification,
        dismissIncidentNotification,
    } = useNotification();

    const notificationItems = useMemo<NotificationCenterItemData[]>(() => {
        const appointments: NotificationCenterItemData[] = pendingAppointmentNotifications.map((notif) => ({
            kind: "appointment",
            id: notif.id,
            receivedAt: notif.receivedAt,
            appointmentId: notif.appointmentId,
            brandName: notif.brandName,
            leadId: notif.type === AppointmentNotificationType.STATUS_UPDATE ? notif.leadId : undefined,
        }));

        const incidents: NotificationCenterItemData[] = pendingIncidentNotifications.map((notif) => ({
            kind: "incident",
            id: notif.id,
            receivedAt: notif.receivedAt,
            incidentId: notif.incidentId,
            title: notif.title,
            body: notif.body,
        }));

        return [...appointments, ...incidents].sort((a, b) => b.receivedAt.toMillis() - a.receivedAt.toMillis());
    }, [pendingAppointmentNotifications, pendingIncidentNotifications]);

    const pendingCount = notificationItems.length;

    const handleAppointmentClick = (notif: Extract<NotificationCenterItemData, { kind: "appointment" }>) => {
        setOpen(false);

        if (notif.leadId) {
            openLeadPanel(notif.leadId);
            return;
        }

        toast.error(t("no_lead_id_available"));
    };

    if (isCallActive() || isMobile || isSafariBrowser()) {
        return null;
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                        "relative h-9 w-9 border-border/80 p-0 shadow-sm transition-all duration-300",
                        "hover:bg-muted/80 hover:shadow-md",
                        pendingCount > 0 &&
                            "border-amber-400/60 bg-gradient-to-br from-amber-500/10 to-orange-500/5 dark:border-amber-500/40"
                    )}
                    aria-label={t("notifications")}
                >
                    <Icon
                        icon={TbBell}
                        size={20}
                        className={cn(
                            "text-foreground/80 transition-transform duration-300",
                            open && "rotate-12 scale-110",
                            pendingCount > 0 && "text-amber-600 dark:text-amber-400"
                        )}
                    />
                    {pendingCount > 0 && (
                        <>
                            <span className="absolute inset-0 rounded-md bg-amber-400/20 animate-ping" aria-hidden />
                            <span
                                className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-amber-950 shadow-sm ring-2 ring-background"
                                data-testid="pending-calls-badge"
                            >
                                {pendingCount > 9 ? "9+" : pendingCount}
                            </span>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border-border/60 p-0 shadow-xl"
                align="end"
                sideOffset={10}
                forceMount
                side="bottom"
            >
                <div className="border-b border-border/60 bg-gradient-to-br from-muted/50 via-background to-background px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20">
                                <Icon icon={TbBell} size={18} className="text-primary" />
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold leading-none tracking-tight">
                                    {t("notifications")}
                                </h4>
                                <p className="mt-1 text-[11px] text-muted-foreground">
                                    {pendingCount === 0
                                        ? t("no_notifications")
                                        : `${pendingCount} ${pendingCount === 1 ? "item" : "items"}`}
                                </p>
                            </div>
                        </div>
                        {pendingCount > 0 && (
                            <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
                                {pendingCount}
                            </span>
                        )}
                    </div>
                </div>

                <div className="max-h-[min(24rem,60vh)] overflow-y-auto p-3">
                    {pendingCount === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-3 px-4 py-10 text-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/80 ring-1 ring-border/60">
                                <Icon icon={TbBellOff} size={28} className="text-muted-foreground/50" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-foreground/80">{t("no_notifications")}</p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    {t("notification_center_empty_hint", {
                                        defaultValue: "You're all caught up for now.",
                                    })}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <ul className="flex flex-col gap-2">
                            {notificationItems.map((notif) => (
                                <li key={`${notif.kind}-${notif.id}`}>
                                    {notif.kind === "appointment" ? (
                                        <NotificationCenterItem
                                            variant="appointment"
                                            title={t("pending_appointments")}
                                            meta={[
                                                {
                                                    label: t("appointment_label", { defaultValue: "Appointment" }),
                                                    value: notif.appointmentId || t("not_available"),
                                                },
                                                {
                                                    label: t("brand_label", { defaultValue: "Brand" }),
                                                    value: notif.brandName || t("not_available"),
                                                },
                                            ]}
                                            receivedAt={notif.receivedAt}
                                            onClick={() => handleAppointmentClick(notif)}
                                            onDismiss={() => dismissAppointmentNotification(notif.id)}
                                        />
                                    ) : (
                                        <NotificationCenterItem
                                            variant="incident"
                                            title={notif.title}
                                            description={notif.body}
                                            receivedAt={notif.receivedAt}
                                            onDismiss={() => dismissIncidentNotification(notif.incidentId)}
                                        />
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};
